<template>
  <div class="payment-view container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-pink">Payments Management</h2>

      <div class="row g-3 mb-4">
        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedGrade"
            @change="onGradeChange"
          >
            <option value="">Select Grade</option>
            <option v-for="g in grades" :key="g.id" :value="g.id">
              {{ g.level }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <select
            class="form-select"
            v-model="selectedSubject"
            @change="onSubjectChange"
            :disabled="!selectedGrade"
          >
            <option value="">Select Subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedStatus"
            @change="fetchPayments"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <button
        type="button"
        class="btn btn-pink"
        data-bs-toggle="modal"
        data-bs-target="#addPaymentModal"
      >
        <i class="bi bi-plus-lg"></i> Add Payment
      </button>
    </div>

    <!-- Add Payment Modal -->
    <div
      class="modal fade"
      id="addPaymentModal"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title">Add Payment</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <AddPayment @payment-added="onPaymentAdded" />
        </div>
      </div>
    </div>
    <p class="text-end text-pink mb-2">{{ totalPayments }} Payments</p>
    <!-- Payments Table -->
    <!-- Payments Table -->
    <div class="table-responsive mt-3">
      <table class="table table-dark table-hover align-middle">
        <thead class="text-pink">
          <tr>
            <!-- <th>ID</th> -->
            <th>Student Name</th>
            <th>Amount</th>
            <th>Payment Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <!-- 🔄 LOADING -->
          <tr v-if="loading">
            <td colspan="6" class="text-center py-5">
              <div class="spinner-border text-light"></div>
              <p class="mt-2">Loading payments...</p>
            </td>
          </tr>

          <!-- ❌ NO DATA -->
          <tr v-else-if="payments.length === 0">
            <td colspan="6" class="text-center text-light">
              No payments available
            </td>
          </tr>

          <!-- ✅ DATA -->
          <tr v-else v-for="payment in payments" :key="payment.id">
            <!-- <td>{{ payment.id }}</td> -->

            <td>
              {{
                payment.student
                  ? payment.student.first_name + " " + payment.student.last_name
                  : "-"
              }}
            </td>

            <td>{{ formatCurrency(payment.amount) }}</td>
            <td>{{ payment.payment_date }}</td>

            <td>
              <span
                :class="{
                  'text-success': payment.status === 'completed',
                  'text-warning': payment.status === 'pending',
                  'text-danger': payment.status === 'failed',
                }"
              >
                {{ payment.status }}
              </span>
            </td>

            <td>
              <button
                @click="togglePaid(payment)"
                class="btn btn-sm btn-outline-info me-1"
              >
                <i
                  class="bi"
                  :class="
                    payment.status === 'completed'
                      ? 'bi-x-circle'
                      : 'bi-check-circle'
                  "
                ></i>
              </button>

              <button
                @click="deletePayment(payment.id)"
                class="btn btn-sm btn-outline-danger"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="d-flex justify-content-center mt-4">
        <nav v-if="lastPage > 1" class="Video pagination">
          <ul class="pagination custom-pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="fetchPayments(currentPage - 1)">
                Previous
              </button>
            </li>

            <li
              v-for="page in lastPage"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="fetchPayments(page)">
                {{ page }}
              </button>
            </li>

            <li
              class="page-item"
              :class="{ disabled: currentPage === lastPage }"
            >
              <button class="page-link" @click="fetchPayments(currentPage + 1)">
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddPayment from "./PaymentParts/AddPayment.vue";

export default {
  name: "PaymentView",
  components: { AddPayment },
  data() {
    return {
      payments: [],
      grades: [],
      subjects: [],
      selectedGrade: "",
      selectedSubject: "",
      selectedStatus: "",
      loading: false,
      currentPage: 1,
      lastPage: 1,
      totalPayments: 0,
    };
  },
  methods: {
    async loadGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async loadSubjects(gradeId) {
      try {
        const res = await axios.get(`/a-grade-subjects?grade_id=${gradeId}`);
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
        this.subjects = [];
      }
    },

    async onGradeChange() {
      this.selectedSubject = "";
      await this.loadSubjects(this.selectedGrade);
      await this.fetchPayments();
    },

    async onSubjectChange() {
      await this.fetchPayments();
    },

    async fetchPayments() {
      this.loading = true; // ✅ start loading

      try {
        const params = {};
        if (this.selectedGrade) params.grade_id = this.selectedGrade;
        if (this.selectedSubject) params.subject_id = this.selectedSubject;
        if (this.selectedStatus) params.status = this.selectedStatus;

        const res = await axios.get("/get-payments", { params });
        this.payments = res.data.data;
        this.currentPage = res.data.current_page;
        this.lastPage = res.data.last_page;
        this.totalPayments = res.data.total;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false; // ✅ stop loading ALWAYS
      }
    },
    async deletePayment(id) {
      if (!confirm("Are you sure you want to delete this payment?")) return;
      try {
        await axios.delete(`/delete-payments/${id}`);
        this.payments = this.payments.filter((p) => p.id !== id);
      } catch (err) {
        console.error(err);
      }
    },
    async togglePaid(payment) {
      try {
        const res = await axios.patch(
          `/tg-status-payments/${payment.id}/toggle`,
        );
        payment.status = res.data.status; // updated
      } catch (err) {
        console.error(err);
      }
    },
    formatCurrency(value) {
      return "Rs. " + parseFloat(value).toFixed(2);
    },
    onPaymentAdded() {
      this.fetchPayments();
    },
  },
  watch: {
    selectedStatus() {
      this.fetchPayments();
    },
  },
  mounted() {
    this.loadGrades();
    this.fetchPayments();
  },
};
</script>

<style scoped>
.text-pink {
  color: #ff00cc !important;
}
.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
}
.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}
.border-pink {
  border-color: #ff00cc !important;
}
.border-bottom-pink {
  border-bottom: 2px solid #ff00cc !important;
}

/* Pagination Theme */
.custom-pagination .page-link {
  background-color: #1a1a1a;
  color: #ff00cc;
  border: 1px solid #333;
  transition: 0.3s;
}

.custom-pagination .page-link:hover {
  background-color: #ff00cc;
  color: #fff;
  border-color: #ff00cc;
}

/* Active Page */
.custom-pagination .page-item.active .page-link {
  background-color: #ff00cc;
  border-color: #ff00cc;
  color: white;
  font-weight: bold;
}

/* Disabled */
.custom-pagination .page-item.disabled .page-link {
  background-color: #111;
  color: #666;
  border-color: #222;
  cursor: not-allowed;
}
</style>
